import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { HiPencilSquare } from "react-icons/hi2";
import api from "../config/api";
import AddEnterpriseModal from "../components/AddEnterpriseModal";
import EditEnterpriseModal from "../components/EditEnterpriseModal";
import "./Enterprises.css";
import "../styles/buttons.css";
import Select from "react-select";
import { toast } from "react-toastify";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import { LANGUAGE_OPTIONS, formatLanguage } from "../utils/language";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from "../utils/pagination";

interface Enterprise {
  id: number;
  nom: string;
  tax: string;
  sirene?: string;
  adresse?: string;
  mail?: string;
  country: string;
  language: string;
  sendContracts?: boolean;
  coders?: Array<{
    id: number;
    first_name: string;
    last_name: string;
  }>;
}

/** The filters the API applies. */
interface EnterpriseQuery {
  search: string;
  country: string;
  language: string;
  sendContracts: string;
  anomaly: string;
}

const SEND_CONTRACTS_OPTIONS: { value: string; label: string }[] = [
  { value: "true", label: "Activé" },
  { value: "false", label: "Désactivé" },
];

/**
 * Conditions worth singling out, each matching a figure on the dashboard card.
 *
 * One dropdown rather than two independent toggles: the card links to one at a time, and
 * a single-select is what the rest of this filter bar looks like. Combining the two is
 * not offered — an enterprise that is both is rare enough not to justify the control.
 */
const ANOMALY_OPTIONS: { value: string; label: string }[] = [
  { value: "missingMail", label: "Sans email" },
  { value: "withoutCoder", label: "Sans codeur" },
];

/**
 * A filter dropdown matching the other list pages: react-select with the shared
 * dark styles, rendered in a portal so the menu is not clipped by the filter
 * panel. The empty option acts as "no filter" and is what the placeholder shows.
 */
const FilterSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: (string | { value: string; label: string })[];
}> = ({ value, onChange, placeholder, options }) => {
  const normalised = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const selected = normalised.find((option) => option.value === value) ?? null;

  return (
    <Select
      value={selected}
      onChange={(option) => onChange(option ? option.value : "")}
      options={[{ value: "", label: placeholder }, ...normalised]}
      styles={darkSelectStyles}
      placeholder={placeholder}
      isSearchable={false}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  );
};

const Enterprises: React.FC = () => {
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] =
    useState<Enterprise | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [sendContractsFilter, setSendContractsFilter] = useState("all");

  // `?missingMail=true` / `?withoutCoder=true` open the page on one of those sets — how
  // the dashboard's Entreprises card links to them. Read once, as the initial state, so
  // the filter can then be changed without the URL pulling it back.
  const [searchParams] = useSearchParams();
  const [anomalyFilter, setAnomalyFilter] = useState(() => {
    if (searchParams.get("missingMail") === "true") return "missingMail";
    if (searchParams.get("withoutCoder") === "true") return "withoutCoder";
    return "";
  });

  // Country is free text in the database, so its filter choices come from the API
  // rather than a hardcoded list: an enterprise whose country was not in that list
  // could never be filtered, the comparison being an exact match.
  const [filterOptions, setFilterOptions] = useState<{ countries: string[] }>({
    countries: [],
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/enterprises/filter-options");
        setFilterOptions(response.data);
      } catch (err) {
        console.error("Error loading enterprise filter options:", err);
      }
    };
    loadFilterOptions();
  }, []);

  // Debounced copy of the search box: it is sent to the API, so it must not fire a
  // request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<EnterpriseQuery>(
    () => ({
      search: debouncedSearchTerm,
      country: countryFilter,
      language: languageFilter,
      sendContracts: sendContractsFilter,
      anomaly: anomalyFilter,
    }),
    [
      debouncedSearchTerm,
      countryFilter,
      languageFilter,
      sendContractsFilter,
      anomalyFilter,
    ],
  );

  // Only the very first load shows the page-level spinner. Later fetches must
  // reload the rows in place: the spinner replaces the whole page, which would
  // unmount the search box and take the focus away mid-typing.
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    fetchEnterprises(currentPage, pageSize, activeQuery, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `query` is matched by the API across every enterprise, not only the rows of
   * the current page.
   *
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   */
  const fetchEnterprises = async (
    page: number,
    limit: number,
    query: EnterpriseQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (query.search) params.append("search", query.search);
      if (query.country) params.append("country", query.country);
      if (query.language) params.append("language", query.language);
      if (query.sendContracts !== "all")
        params.append("sendContracts", query.sendContracts);
      // The value is the API's own parameter name, so the two cannot drift apart.
      if (query.anomaly) params.append(query.anomaly, "true");

      const response = await api.get(`/api/enterprises?${params.toString()}`);
      const { data, pagination } = response.data;

      setEnterprises(data);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
      setCurrentPage(pagination.page);
    } catch (err) {
      if (silent) {
        toast.error("Erreur lors du rechargement des entreprises");
      } else {
        setError("Erreur lors du chargement des entreprises");
      }
      console.error("Error loading enterprises:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Reload rather than patch the list in place. A created enterprise cannot carry
  // its coders — the modal links them with separate calls after the POST returns —
  // and a saved one may no longer match the active filters or belong on this page.
  const handleAddEnterprise = () => {
    setIsAddModalOpen(false);
    fetchEnterprises(currentPage, pageSize, activeQuery, true);
  };

  const handleEditEnterprise = (enterprise: Enterprise) => {
    setSelectedEnterprise(enterprise);
    setIsEditModalOpen(true);
  };

  const handleUpdateEnterprise = () => {
    setIsEditModalOpen(false);
    setSelectedEnterprise(null);
    fetchEnterprises(currentPage, pageSize, activeQuery, true);
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleFilterChange = (
    setter: (value: string) => void,
    value: string,
  ) => {
    setter(value);
    setCurrentPage(1);
  };

  // Filter handlers
  const resetFilters = () => {
    setSearchTerm("");
    setCountryFilter("");
    setLanguageFilter("");
    setSendContractsFilter("all");
    setAnomalyFilter("");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchTerm ||
    countryFilter ||
    languageFilter ||
    sendContractsFilter !== "all" ||
    anomalyFilter !== "";

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des entreprises...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchEnterprises(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="enterprises-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Entreprises</h1>
            </div>
            <button
              className="btn-primary"
              onClick={() => setIsAddModalOpen(true)}
            >
              + Ajouter une Entreprise
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-container">
            <div className="filter-group">
              <label>Recherche</label>
              <input
                type="text"
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) =>
                  handleFilterChange(setSearchTerm, e.target.value)
                }
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Pays</label>
              <FilterSelect
                value={countryFilter}
                onChange={(value) => handleFilterChange(setCountryFilter, value)}
                placeholder="Tous les pays"
                options={filterOptions.countries}
              />
            </div>

            <div className="filter-group">
              <label>Langue</label>
              <FilterSelect
                value={languageFilter}
                onChange={(value) => handleFilterChange(setLanguageFilter, value)}
                placeholder="Toutes les langues"
                options={LANGUAGE_OPTIONS}
              />
            </div>

            <div className="filter-group">
              <label>Envoi Contrats</label>
              <FilterSelect
                value={sendContractsFilter}
                onChange={(value) =>
                  handleFilterChange(setSendContractsFilter, value || "all")
                }
                placeholder="Tous"
                options={SEND_CONTRACTS_OPTIONS}
              />
            </div>

            <div className="filter-group">
              <label>Anomalie</label>
              <FilterSelect
                value={anomalyFilter}
                onChange={(value) =>
                  handleFilterChange(setAnomalyFilter, value || "")
                }
                placeholder="Aucune"
                options={ANOMALY_OPTIONS}
              />
            </div>

            <div className="filter-group">
              <button
                onClick={resetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="enterprises-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>SIREN</th>
                  <th>Email</th>
                  <th>Pays</th>
                  <th>Langue</th>
                  <th>Codeurs</th>
                  <th>Envoi Contrats</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enterprises.map((enterprise) => (
                  <tr key={enterprise.id}>
                    <td>{enterprise.id}</td>
                    <td>{enterprise.nom}</td>
                    <td>{enterprise.sirene || EMPTY_VALUE}</td>
                    <td>{enterprise.mail || EMPTY_VALUE}</td>
                    <td>{enterprise.country || EMPTY_VALUE}</td>
                    <td>{formatLanguage(enterprise.language)}</td>
                    <td>
                      {enterprise.coders && enterprise.coders.length > 0 ? (
                        <div className="coders-list">
                          {enterprise.coders.map((coder, index) => (
                            <span key={coder.id} className="coder-tag">
                              {coder.first_name} {coder.last_name}
                              {index < enterprise.coders!.length - 1 && ", "}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-coders">Aucun codeur</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          enterprise.sendContracts === false
                            ? "inactive"
                            : "active"
                        }`}
                      >
                        {enterprise.sendContracts === false
                          ? "Désactivé"
                          : "Activé"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditEnterprise(enterprise)}
                        title="Modifier l'entreprise"
                      >
                        <HiPencilSquare />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Page {currentPage} sur {totalPages} ({totalItems} entreprises au
                total)
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
</select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddEnterpriseModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddEnterprise}
      />

      {selectedEnterprise && (
        <EditEnterpriseModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedEnterprise(null);
          }}
          onSave={handleUpdateEnterprise}
          enterprise={selectedEnterprise}
        />
      )}
    </div>
  );
};

export default Enterprises;
